// Data display card: big number + unit + label + optional delta.
// Used on the AI page sidebar and Sessions summary cards.
// Uses JetBrains Mono for the number so it reads as scientific/precise.

import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../theme';

interface StatCardProps {
  label: string;
  value: string | number;
  unit?: string;
  delta?: { value: number; positiveIsGood?: boolean } | null;
  tone?: 'default' | 'brand' | 'data';
  mini?: boolean;
  /** Wave 20.49l — optional tap target. When set, the card becomes
   *  a button (cursor pointer, brand-bordered on hover). Used by
   *  the aero session baseline card so a rider can tap to recapture
   *  the baseline mid-session. */
  onClick?: () => void;
  /** Native title attribute; renders as a browser tooltip on hover. */
  title?: string;
}

export default function StatCard({
  label,
  value,
  unit,
  delta,
  tone = 'default',
  mini = false,
  onClick,
  title,
}: StatCardProps) {
  const accent =
    tone === 'brand' ? colors.brand : tone === 'data' ? colors.data : colors.text;
  const clickable = !!onClick;

  return (
    <Box
      onClick={onClick}
      title={title}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!();
              }
            }
          : undefined
      }
      sx={{
        p: mini ? 2 : 2.5,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        transition: 'border-color 220ms',
        cursor: clickable ? 'pointer' : undefined,
        '&:hover': {
          borderColor: clickable ? colors.brand : colors.borderStrong,
        },
      }}
    >
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: mini ? '1.5rem' : '2rem',
            fontWeight: 700,
            color: accent,
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}
        >
          {value}
        </Typography>
        {unit && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              color: colors.textMuted,
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {unit}
          </Typography>
        )}
      </Box>
      {delta && (
        <Typography
          sx={{
            mt: 1,
            fontSize: '0.75rem',
            fontWeight: 600,
            color:
              (delta.value > 0) === (delta.positiveIsGood ?? true)
                ? colors.brand
                : colors.danger,
            fontFamily: '"JetBrains Mono", monospace',
          }}
        >
          {delta.value > 0 ? '↑' : '↓'} {Math.abs(delta.value).toFixed(1)}
        </Typography>
      )}
    </Box>
  );
}
